import { z } from 'zod';
import { productTechSpecsSchema } from './pv/schemas';
import { itemsLayoutSchema } from './items-layout';
import {
  CATEGORY_KINDS,
  CUSTOMER_TYPES,
  DISCOUNT_TYPES,
  ITEM_ROLES,
  LOAN_METHODS,
  PANEL_PRICE_MODES,
  POST_STATUSES,
  PRICING_METHODS,
  PRODUCT_STATUSES,
  QUOTE_STATUSES,
  RATE_CONVERSIONS,
  TEMPLATE_KINDS,
} from './enums';

// ---------- Nguyên tố ----------
const requiredText = (max: number, label: string) =>
  z
    .string({ error: `${label} là bắt buộc` })
    .trim()
    .min(1, `${label} là bắt buộc`)
    .max(max, `${label} tối đa ${max} ký tự`);
const optionalText = (max: number) =>
  z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null ? null : v.trim() === '' ? null : v.trim()))
    .pipe(z.string().max(max).nullable())
    .default(null);

/** Số tiền VND (chuỗi hoặc số), tối đa 2 chữ số thập phân, không âm. */
export const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim().replace(/[\s,]/g, ''))
  .pipe(z.string().regex(/^\d{1,15}(\.\d{1,2})?$/, 'Số tiền không hợp lệ'));
export const optionalMoney = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => (v == null || String(v).trim() === '' ? null : String(v).trim().replace(/[\s,]/g, '')))
  .pipe(
    z
      .string()
      .regex(/^\d{1,15}(\.\d{1,2})?$/, 'Số tiền không hợp lệ')
      .nullable(),
  )
  .default(null);
/** Số thập phân không âm dạng chuỗi (%, kW, kWh...). */
export const decimalStr = (maxInt = 9, dp = 4) =>
  z
    .union([z.string(), z.number()])
    .transform((v) => String(v).trim().replace(',', '.'))
    .pipe(z.string().regex(new RegExp(`^\\d{1,${maxInt}}(\\.\\d{1,${dp}})?$`), 'Giá trị số không hợp lệ'));
export const optionalDecimalStr = (maxInt = 9, dp = 4) =>
  z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v) => (v == null || String(v).trim() === '' ? null : String(v).trim().replace(',', '.')))
    .pipe(
      z
        .string()
        .regex(new RegExp(`^\\d{1,${maxInt}}(\\.\\d{1,${dp}})?$`), 'Giá trị số không hợp lệ')
        .nullable(),
    )
    .default(null);
export const percent = decimalStr(3, 4).refine((v) => Number(v) <= 100, 'Tỷ lệ phần trăm không vượt quá 100');
const intRange = (min: number, max: number) => z.coerce.number().int('Phải là số nguyên').min(min).max(max);
export const uuid = z.uuid('ID không hợp lệ');
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày không hợp lệ (yyyy-mm-dd)');

/** Số điện thoại Việt Nam: di động 10 số (03/05/07/08/09) hoặc cố định 11 số (02x), chấp nhận +84. */
export const VN_PHONE_REGEX = /^(?:\+84|0)(?:[35789]\d{8}|2\d{9})$/;
export const normalizePhone = (s: string) => s.replace(/[\s.\-()]/g, '');
export const vnPhone = z
  .string()
  .transform((s) => normalizePhone(s))
  .pipe(z.string().regex(VN_PHONE_REGEX, 'Số điện thoại không hợp lệ (VD: 0912345678)'));
export const optionalVnPhone = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((v) => (v == null || v.trim() === '' ? null : normalizePhone(v)))
  .pipe(z.string().regex(VN_PHONE_REGEX, 'Số điện thoại không hợp lệ').nullable())
  .default(null);
const optionalEmail = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((v) => (v == null || v.trim() === '' ? null : v.trim().toLowerCase()))
  .pipe(z.email('Email không hợp lệ').nullable())
  .default(null);

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(20),
  q: z.string().trim().max(200).optional(),
  sort: z.string().max(50).optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

// ---------- Xác thực & người dùng ----------
export const loginSchema = z.object({
  login: requiredText(200, 'Tên đăng nhập/email'),
  password: z.string().min(1, 'Mật khẩu là bắt buộc').max(200),
});

/** Chính sách mật khẩu: ≥ 8 ký tự, có chữ hoa, chữ thường, chữ số. */
export const passwordPolicy = z
  .string()
  .min(8, 'Mật khẩu tối thiểu 8 ký tự')
  .max(128)
  .refine(
    (p) => /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p),
    'Mật khẩu phải có chữ hoa, chữ thường và chữ số',
  );

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Nhập mật khẩu hiện tại'),
  newPassword: passwordPolicy,
});

export const userCreateSchema = z.object({
  username: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9._-]{3,50}$/, 'Tên đăng nhập 3–50 ký tự: chữ thường, số, dấu . _ -'),
  email: optionalEmail,
  fullName: requiredText(150, 'Họ tên'),
  title: optionalText(100),
  phone: optionalVnPhone,
  officeRegionId: z.string().trim().min(1).max(30),
  password: passwordPolicy,
  roleIds: z.array(uuid).min(1, 'Chọn ít nhất một vai trò'),
  teamId: uuid.nullable().optional(),
  mustChangePassword: z.boolean().default(true),
});
export const userUpdateSchema = z.object({
  email: optionalEmail,
  fullName: requiredText(150, 'Họ tên'),
  title: optionalText(100),
  phone: optionalVnPhone,
  officeRegionId: z.string().trim().min(1).max(30).optional(),
  roleIds: z.array(uuid).min(1, 'Chọn ít nhất một vai trò'),
  teamId: uuid.nullable().optional(),
});
export const resetPasswordSchema = z.object({
  password: passwordPolicy,
  mustChangePassword: z.boolean().default(true),
});
export const profileUpdateSchema = z.object({
  fullName: requiredText(150, 'Họ tên'),
  title: optionalText(100),
  phone: optionalVnPhone,
});

export const teamSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,30}$/, 'Mã đội: 2–30 ký tự in hoa, số, - _'),
  name: requiredText(150, 'Tên đội'),
  description: optionalText(500),
  isActive: z.boolean().default(true),
  managerIds: z.array(uuid).max(20).default([]),
});

export const statsQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  groupBy: z.enum(['day', 'week', 'month']).default('month'),
  teamId: uuid.optional(),
  ownerId: uuid.optional(),
});

export const roleSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_]{2,40}$/, 'Mã vai trò: chữ in hoa, số, _'),
  name: requiredText(100, 'Tên vai trò'),
  description: optionalText(300),
  permissions: z.array(z.string().max(60)),
});

// ---------- Danh mục ----------
export const manufacturerSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,30}$/, 'Mã hãng: 2–30 ký tự in hoa, số, - _'),
  name: requiredText(150, 'Tên hãng'),
  country: optionalText(100),
  website: optionalText(300),
  note: optionalText(1000),
  isActive: z.boolean().default(true),
});

export const categorySchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,40}$/, 'Mã danh mục không hợp lệ'),
  name: requiredText(150, 'Tên danh mục'),
  kind: z.enum(CATEGORY_KINDS),
  sortOrder: z.coerce.number().int().default(0),
  isActive: z.boolean().default(true),
});

export const productSchema = z.object({
  code: z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim().toUpperCase()))
    .pipe(
      z
        .string()
        .regex(/^[A-Z0-9._-]{2,40}$/, 'Mã vật tư không hợp lệ')
        .nullable(),
    )
    .default(null),
  name: requiredText(250, 'Tên sản phẩm'),
  categoryId: uuid,
  manufacturerId: uuid.nullable().optional(),
  model: optionalText(150),
  specs: z.string().trim().max(5000).default(''),
  /** Thông số có cấu trúc cho tính toán kỹ thuật; vắng mặt khi sửa = giữ nguyên, null = xóa */
  techSpecs: productTechSpecsSchema.nullable().optional(),
  ratedPowerW: optionalDecimalStr(7, 2),
  capacityKwh: optionalDecimalStr(5, 3),
  unit: requiredText(30, 'Đơn vị tính'),
  unitPrice: money,
  costPrice: optionalMoney.optional(),
  description: optionalText(5000),
  status: z.enum(PRODUCT_STATUSES).default('ACTIVE'),
  priceChangeNote: optionalText(300).optional(),
});

export const compatibilitySchema = z
  .object({
    inverterManufacturerId: uuid.nullable().optional(),
    inverterProductId: uuid.nullable().optional(),
    batteryManufacturerId: uuid.nullable().optional(),
    batteryProductId: uuid.nullable().optional(),
    /** Điều kiện AND theo danh mục — chỉ dùng với quy tắc theo hãng (model đã xác định danh mục) */
    inverterCategoryId: uuid.nullable().optional(),
    batteryCategoryId: uuid.nullable().optional(),
    allowed: z.boolean().default(true),
    protocol: optionalText(200),
    voltage: optionalText(100),
    conditions: optionalText(2000),
  })
  .refine((v) => !!v.inverterManufacturerId !== !!v.inverterProductId, {
    message: 'Chọn đúng một: hãng biến tần HOẶC model biến tần',
    path: ['inverterProductId'],
  })
  .refine((v) => !!v.batteryManufacturerId !== !!v.batteryProductId, {
    message: 'Chọn đúng một: hãng pin HOẶC model pin',
    path: ['batteryProductId'],
  })
  .refine((v) => !(v.inverterCategoryId && v.inverterProductId), {
    message: 'Điều kiện danh mục chỉ dùng khi áp dụng theo hãng biến tần',
    path: ['inverterCategoryId'],
  })
  .refine((v) => !(v.batteryCategoryId && v.batteryProductId), {
    message: 'Điều kiện danh mục chỉ dùng khi áp dụng theo hãng pin',
    path: ['batteryCategoryId'],
  });

// ---------- Gói sản phẩm ----------
export const moneyLineSchema = z.object({ label: requiredText(150, 'Tên khoản'), amount: money });

export const packageItemSchema = z.object({
  productId: uuid,
  role: z.enum(ITEM_ROLES).default('OTHER'),
  quantity: decimalStr(6, 2).refine((v) => Number(v) > 0, 'Số lượng phải lớn hơn 0'),
  unitPriceOverride: optionalMoney.optional(),
  note: optionalText(500),
});

export const PACKAGE_OPTION_KINDS = ['SEGMENT', 'SYSTEM_TYPE'] as const;
export type PackageOptionKind = (typeof PACKAGE_OPTION_KINDS)[number];

/** Một mục trong danh sách "Danh mục gói" / "Loại hệ thống" của gói sản phẩm */
export const packageOptionSchema = z.object({
  kind: z.enum(PACKAGE_OPTION_KINDS),
  /** Bỏ trống ⇒ tự sinh; không đổi được sau khi tạo */
  code: z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim().toUpperCase()))
    .pipe(z.string().regex(/^[A-Z0-9_-]{2,40}$/, 'Mã không hợp lệ (chữ, số, _ -)').nullable())
    .default(null),
  name: requiredText(100, 'Tên'),
  sortOrder: z.coerce.number().int().default(0),
  isActive: z.boolean().default(true),
});

export const packageCreateSchema = z.object({
  code: z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim().toUpperCase()))
    .pipe(
      z
        .string()
        .regex(/^[A-Z0-9._-]{2,40}$/, 'Mã gói không hợp lệ')
        .nullable(),
    )
    .default(null),
  name: requiredText(200, 'Tên gói'),
  segment: optionalText(100),
});

export const packageVersionSchema = z
  .object({
    name: requiredText(200, 'Tên gói'),
    segment: optionalText(100),
    description: optionalText(5000),
    imageFileId: uuid.nullable().optional(),
    /** Mã loại hệ thống (danh sách quản lý tại trang Gói sản phẩm) */
    systemType: z.string().trim().min(1, 'Chọn loại hệ thống').max(40),
    capacityKwp: decimalStr(5, 3),
    pricingMethod: z.enum(PRICING_METHODS),
    fixedPrice: optionalMoney,
    /** Đơn giá theo kWp (phương pháp PER_KWP) */
    pricePerKwp: optionalMoney.optional(),
    expectedCost: optionalMoney.optional(),
    markupPercent: decimalStr(4, 4).default('0'),
    extraCosts: z.array(moneyLineSchema).max(20).default([]),
    panelDefaultQty: intRange(0, 10000).default(0),
    panelMinQty: intRange(0, 10000).default(0),
    panelMaxQty: intRange(0, 10000).default(0),
    panelStep: intRange(1, 1000).default(1),
    panelAdjustable: z.boolean().default(false),
    panelPriceMode: z.enum(PANEL_PRICE_MODES).default('CUSTOM_UNIT_PRICE'),
    /** Đơn giá chênh lệch mỗi tấm khi TĂNG số tấm (trống ⇒ mặc định trong Cài đặt) */
    panelCustomUnitPrice: optionalMoney,
    /** Đơn giá chênh lệch mỗi tấm khi GIẢM số tấm (trống ⇒ mặc định trong Cài đặt) */
    panelCustomDecreasePrice: optionalMoney.optional(),
    batteryOptionEnabled: z.boolean().default(false),
    batteryMinQty: intRange(0, 100).default(1),
    batteryMaxQty: intRange(0, 100).default(1),
    notes: optionalText(2000),
    items: z.array(packageItemSchema).max(300),
  })
  .superRefine((v, ctx) => {
    if (v.pricingMethod === 'FIXED' && v.fixedPrice == null)
      ctx.addIssue({ code: 'custom', message: 'Gói giá cố định phải có giá bán', path: ['fixedPrice'] });
    if (v.pricingMethod === 'PER_KWP' && v.pricePerKwp == null)
      ctx.addIssue({ code: 'custom', message: 'Gói tính theo kWp phải có đơn giá theo kWp', path: ['pricePerKwp'] });
    const panels = v.items.filter((i) => i.role === 'PANEL');
    const invs = v.items.filter((i) => i.role === 'INVERTER');
    if (panels.length > 1)
      ctx.addIssue({ code: 'custom', message: 'Chỉ được có một dòng "Tấm pin chính"', path: ['items'] });
    if (invs.length > 1)
      ctx.addIssue({ code: 'custom', message: 'Chỉ được có một dòng "Biến tần chính"', path: ['items'] });
    if (panels.length === 1) {
      if (v.panelDefaultQty < 1)
        ctx.addIssue({ code: 'custom', message: 'Số tấm pin mặc định phải ≥ 1', path: ['panelDefaultQty'] });
      if (!(v.panelMinQty <= v.panelDefaultQty && v.panelDefaultQty <= v.panelMaxQty))
        ctx.addIssue({
          code: 'custom',
          message: 'Phải thỏa: tối thiểu ≤ mặc định ≤ tối đa',
          path: ['panelDefaultQty'],
        });
    }
    if (v.batteryOptionEnabled && !(1 <= v.batteryMinQty && v.batteryMinQty <= v.batteryMaxQty))
      ctx.addIssue({
        code: 'custom',
        message: 'Giới hạn số lượng pin lưu trữ không hợp lệ',
        path: ['batteryMinQty'],
      });
  });

/** Vật tư của gói tùy chỉnh: vai trò (tấm pin / biến tần / pin chính) do máy chủ suy ra theo danh mục */
export const customPackageItemSchema = z.object({
  productId: uuid,
  quantity: decimalStr(6, 2).refine((v) => Number(v) > 0, 'Số lượng phải lớn hơn 0'),
  note: optionalText(500),
});
const noDuplicateProducts = (v: { items: { productId: string }[] }, ctx: z.RefinementCtx) => {
  const seen = new Set<string>();
  v.items.forEach((it, i) => {
    if (seen.has(it.productId))
      ctx.addIssue({ code: 'custom', message: 'Vật tư bị trùng — tăng số lượng ở dòng đã có', path: ['items', i, 'productId'] });
    seen.add(it.productId);
  });
};
/** Kiểm tra cấu hình gói tùy chỉnh (chưa lưu): vật tư & loại hệ thống */
export const customPackageCheckSchema = z
  .object({
    systemType: z.string().trim().max(40).default(''),
    items: z.array(customPackageItemSchema).max(100, 'Tối đa 100 dòng vật tư'),
  })
  .superRefine(noDuplicateProducts);
/**
 * GÓI TÙY CHỈNH tạo từ trang lập báo giá: chỉ nhập GIÁ CHÀO của cả gói (không dùng đơn giá vật tư). `requestId` do
 * trình tạo gói sinh một lần ⇒ gửi lại (nhấn lưu nhiều lần, mạng chập chờn) không tạo gói trùng.
 */
export const customPackageSchema = z
  .object({
    requestId: uuid,
    name: requiredText(200, 'Tên gói'),
    description: optionalText(5000),
    segment: optionalText(100),
    systemType: z.string().trim().min(1, 'Chọn loại hệ thống').max(40),
    notes: optionalText(2000),
    offerPrice: money.refine((v) => Number(v) > 0, 'Giá chào gói phải lớn hơn 0'),
    priceNote: optionalText(500),
    items: z.array(customPackageItemSchema).min(1, 'Thêm ít nhất một vật tư').max(100, 'Tối đa 100 dòng vật tư'),
    /** Mẫu đã dùng để điền sẵn (chỉ ghi nhật ký) */
    templateId: uuid.nullish(),
  })
  .superRefine(noDuplicateProducts);
export type CustomPackageInput = z.infer<typeof customPackageSchema>;
/** MẪU GÓI TÙY CHỈNH (quyền package.manage): cấu hình vật tư soạn sẵn, không có giá. */
export const customPackageTemplateSchema = z
  .object({
    name: requiredText(200, 'Tên mẫu'),
    description: optionalText(5000),
    segment: optionalText(100),
    systemType: z.string().trim().min(1, 'Chọn loại hệ thống').max(40),
    notes: optionalText(2000),
    isActive: z.boolean().default(true),
    items: z.array(customPackageItemSchema).min(1, 'Thêm ít nhất một vật tư').max(100, 'Tối đa 100 dòng vật tư'),
  })
  .superRefine(noDuplicateProducts);
export type CustomPackageTemplateInput = z.infer<typeof customPackageTemplateSchema>;

/**
 * Sửa giá hàng loạt (bảng giá gói): mỗi dòng là phương pháp tính giá + giá trị của phương pháp đó. Gói đã phát hành
 * ⇒ phát hành phiên bản mới; gói chưa phát hành ⇒ lưu vào bản nháp.
 */
export const packageBulkPricingSchema = z.object({
  /** Mật khẩu đăng nhập của người thao tác — xác nhận lại trước khi đổi giá */
  password: z.string().min(1, 'Nhập mật khẩu đăng nhập để xác nhận').max(200),
  changeNote: optionalText(500),
  items: z
    .array(
      z
        .object({
          packageId: uuid,
          pricingMethod: z.enum(PRICING_METHODS),
          fixedPrice: optionalMoney.optional(),
          markupPercent: decimalStr(4, 4).nullable().optional(),
          pricePerKwp: optionalMoney.optional(),
          /** Cách tính giá khi đổi số tấm pin (giá cố định / theo kWp); không gửi ⇒ giữ nguyên */
          panelPriceMode: z.enum(PANEL_PRICE_MODES).optional(),
          /** Giá mỗi tấm khi tăng / khi giảm (null ⇒ dùng mặc định trong Cài đặt) */
          panelCustomUnitPrice: optionalMoney.optional(),
          panelCustomDecreasePrice: optionalMoney.optional(),
        })
        .superRefine((v, ctx) => {
          if (v.pricingMethod === 'FIXED' && v.fixedPrice == null)
            ctx.addIssue({ code: 'custom', message: 'Nhập giá bán gói', path: ['fixedPrice'] });
          if (v.pricingMethod === 'ITEMIZED' && v.markupPercent == null)
            ctx.addIssue({ code: 'custom', message: 'Nhập tỷ lệ lợi nhuận', path: ['markupPercent'] });
          if (v.pricingMethod === 'PER_KWP' && v.pricePerKwp == null)
            ctx.addIssue({ code: 'custom', message: 'Nhập đơn giá theo kWp', path: ['pricePerKwp'] });
        }),
    )
    .min(1, 'Chưa có gói nào được sửa giá')
    .max(500)
    .refine((xs) => new Set(xs.map((x) => x.packageId)).size === xs.length, 'Mỗi gói chỉ xuất hiện một lần'),
});
export type PackageBulkPricingInput = z.infer<typeof packageBulkPricingSchema>;

// ---------- Tài chính ----------
export const financingPlanSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9._-]{2,40}$/, 'Mã chương trình không hợp lệ'),
    name: requiredText(200, 'Tên gói vay'),
    lenderName: optionalText(200),
    description: optionalText(3000),
    annualRatePercent: decimalStr(3, 4),
    method: z.enum(LOAN_METHODS),
    rateConversion: z.enum(RATE_CONVERSIONS).default('NOMINAL_DIVIDE_12'),
    minTermMonths: intRange(1, 600),
    maxTermMonths: intRange(1, 600),
    maxFinancingPercent: percent,
    minLoanAmount: optionalMoney,
    maxLoanAmount: optionalMoney,
    minDownPaymentPercent: percent.default('0'),
    processingFeePercent: percent.default('0'),
    prepaymentPenaltyNote: optionalText(1000),
    otherFeesNote: optionalText(1000),
    isActive: z.boolean().default(true),
  })
  .refine((v) => v.minTermMonths <= v.maxTermMonths, {
    message: 'Thời hạn tối thiểu phải ≤ tối đa',
    path: ['maxTermMonths'],
  })
  .refine(
    (v) =>
      v.minLoanAmount == null ||
      v.maxLoanAmount == null ||
      Number(v.minLoanAmount) <= Number(v.maxLoanAmount),
    {
      message: 'Số tiền vay tối thiểu phải ≤ tối đa',
      path: ['maxLoanAmount'],
    },
  );

export const downPaymentSchema = z.object({
  downPaymentMode: z.enum(['PERCENT', 'AMOUNT']).default('PERCENT'),
  downPaymentValue: decimalStr(15, 2).default('0'),
});

export const loanCalcSchema = z
  .object({
    planId: uuid.optional(),
    /** Tùy chỉnh (chỉ dùng cho công cụ tính độc lập khi không chọn chương trình) */
    annualRatePercent: optionalDecimalStr(3, 4).optional(),
    method: z.enum(LOAN_METHODS).optional(),
    rateConversion: z.enum(RATE_CONVERSIONS).optional(),
    systemValue: money,
    termMonths: intRange(1, 600),
    startDate: isoDate.nullable().optional(),
  })
  .extend(downPaymentSchema.shape);

export const paybackInputSchema = z.object({
  enabled: z.boolean().default(false),
  annualProductionKwh: optionalDecimalStr(9, 2),
  specificYieldKwhPerKwp: optionalDecimalStr(5, 2),
  selfConsumptionPercent: optionalDecimalStr(3, 2),
  avoidedTariffPerKwh: optionalDecimalStr(7, 2),
  exportTariffPerKwh: optionalDecimalStr(7, 2),
  exportSharePercent: optionalDecimalStr(3, 2),
  omCostPerYear: optionalMoney,
  tariffEscalationPercent: optionalDecimalStr(3, 2),
  degradationPercent: optionalDecimalStr(2, 2),
  horizonYears: z.coerce.number().int().min(1).max(40).default(25),
});

/**
 * Hệ số sản lượng theo tỉnh/thành = số giờ nắng trung bình/ngày trong năm (kWh/kWp/ngày).
 * Sản lượng năm = công suất × hệ số × 365. Khi lưu, mỗi giá trị phải trong (0; 24].
 */
export const provinceYieldsSchema = z.object({
  yields: z.record(z.string().trim().min(1).max(100), decimalStr(4, 2)),
});
export type ProvinceYields = z.infer<typeof provinceYieldsSchema>;

const foldProvince = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/^(tp\.?|thanh pho|tinh)\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Hệ số sản lượng của tỉnh/thành (so khớp không phân biệt dấu, bỏ tiền tố "TP." / "Tỉnh"). */
export function provinceYieldFor(yields: Record<string, string> | undefined, province: string | null | undefined) {
  if (!yields || !province?.trim()) return null;
  const k = foldProvince(province);
  for (const [name, v] of Object.entries(yields)) if (foldProvince(name) === k) return v;
  return null;
}

// ---------- Báo giá ----------
export const quoteInputSchema = z.object({
  quoteDate: isoDate,
  validUntil: isoDate,
  salesperson: z
    .object({
      name: requiredText(150, 'Tên người báo giá'),
      title: optionalText(100),
      phone: optionalVnPhone,
      email: optionalEmail,
    })
    .nullable()
    .optional(),
  /**
   * Khách hàng: được để trống khi lưu nháp / chọn gói; bắt buộc khi xem trước hoặc phát hành (quoteCustomerIssues).
   * Số điện thoại đã nhập vẫn phải hợp lệ.
   */
  customer: z
    .object({
      name: z.string().trim().max(200, 'Tên khách hàng tối đa 200 ký tự').default(''),
      phone: z
        .union([z.string(), z.null(), z.undefined()])
        .transform((v) => (v == null || v.trim() === '' ? '' : normalizePhone(v)))
        .pipe(z.string().refine((v) => v === '' || VN_PHONE_REGEX.test(v), 'Số điện thoại không hợp lệ (VD: 0912345678)')),
      province: z.string().trim().max(100).default(''),
      address: optionalText(300),
      type: z.enum(CUSTOMER_TYPES).nullable().optional(),
      note: optionalText(1000),
    })
    .default({ name: '', phone: '', province: '', address: null, note: null }),
  packageVersionId: uuid.nullable(),
  /** Ghép thêm gói sản phẩm: giá trị = tổng các gói; khối lượng vật tư cộng dồn (ĐVT "Trọn gói" giữ 1) */
  extraPackageVersionIds: z.array(uuid).max(5, 'Ghép tối đa 5 gói').default([]),
  panelQty: z.number().int().nullable().optional(),
  /** Biến tần thay thế cho biến tần của gói chính (cùng hãng, cùng danh mục); null = theo gói */
  inverterProductId: uuid.nullable().optional(),
  battery: z
    .object({ productId: uuid, quantity: z.number().int().min(1).max(100) })
    .nullable()
    .optional(),
  extraCosts: z.array(moneyLineSchema).max(20).default([]),
  discount: z
    .object({ type: z.enum(DISCOUNT_TYPES).default('PERCENT'), value: decimalStr(15, 4).default('0') })
    .default({ type: 'PERCENT', value: '0' }),
  financing: z
    .object({
      enabled: z.boolean().default(false),
      planId: uuid.nullable().optional(),
      termMonths: z.number().int().min(1).max(600).nullable().optional(),
      startDate: isoDate.nullable().optional(),
    })
    .extend(downPaymentSchema.shape)
    .default({ enabled: false, downPaymentMode: 'PERCENT', downPaymentValue: '0' }),
  payback: paybackInputSchema.default({ enabled: false, horizonYears: 25 } as never),
  /** Bỏ trang "Phương án tài chính tham khảo" khỏi PDF (khách thanh toán trực tiếp luôn bỏ trang này) */
  omitFinancingPage: z.boolean().default(false),
  notes: optionalText(3000),
  internalNote: optionalText(3000),
  /** Bố cục bảng danh mục vật tư trong PDF (ẩn cột, độ rộng cột, độ cao dòng) */
  itemsLayout: itemsLayoutSchema.nullable().optional(),
  /**
   * Xác nhận cảnh báo "gói tùy chỉnh" — mỗi lần chọn một gói tùy chỉnh (gói chính / gói ghép) có một mục với `token`
   * mới. Người xác nhận & thời điểm do MÁY CHỦ ghi khi lưu (giá trị client gửi lên bị bỏ qua).
   */
  customPackageConfirmations: z
    .array(
      z.object({
        packageVersionId: uuid,
        token: z.string().regex(/^[A-Za-z0-9-]{8,64}$/, 'Mã xác nhận không hợp lệ'),
        confirmedAt: z.string().max(40).nullable().optional(),
        confirmedById: uuid.nullable().optional(),
        confirmedByName: z.string().max(150).nullable().optional(),
      }),
    )
    .max(6)
    .default([]),
});
export type QuoteInput = z.infer<typeof quoteInputSchema>;

/** Thông tin khách hàng bắt buộc khi xem trước / phát hành báo giá. */
export function quoteCustomerIssues(c: Pick<QuoteInput['customer'], 'name' | 'phone' | 'province'>): { code: string; message: string; field: string }[] {
  const out: { code: string; message: string; field: string }[] = [];
  if (!c.name.trim()) out.push({ code: 'CUSTOMER_NAME_REQUIRED', message: 'Nhập tên khách hàng', field: 'customer.name' });
  if (!VN_PHONE_REGEX.test(normalizePhone(c.phone || ''))) out.push({ code: 'CUSTOMER_PHONE_REQUIRED', message: 'Nhập số điện thoại khách hàng hợp lệ (VD: 0912345678)', field: 'customer.phone' });
  if (!c.province.trim()) out.push({ code: 'CUSTOMER_PROVINCE_REQUIRED', message: 'Chọn tỉnh/thành phố của khách hàng', field: 'customer.province' });
  return out;
}

/** Bỏ trang "Phương án tài chính tham khảo": khách thanh toán trực tiếp (không vay) hoặc người lập chọn bỏ. */
export const financingPageOmitted = (i: Pick<QuoteInput, 'financing' | 'omitFinancingPage'>) => !i.financing.enabled || i.omitFinancingPage;

export const quoteStatusChangeSchema = z.object({ status: z.enum(QUOTE_STATUSES), note: optionalText(500) });
export const quoteIssueSchema = z.object({
  confirmWarnings: z.boolean().default(false),
  expectedLockVersion: z.number().int(),
});

// ---------- Bản tin ----------
export const postSchema = z.object({
  title: requiredText(250, 'Tiêu đề'),
  content: z.string().trim().max(20000).default(''),
  linkUrl: z
    .union([z.string(), z.null(), z.undefined()])
    .transform((v) => (v == null || v.trim() === '' ? null : v.trim()))
    .pipe(
      z
        .string()
        .max(1000)
        .refine((u) => /^https?:\/\//i.test(u), 'Liên kết phải bắt đầu bằng http:// hoặc https://')
        .nullable(),
    )
    .default(null),
  status: z.enum(POST_STATUSES).default('DRAFT'),
  isPinned: z.boolean().default(false),
  allowComments: z.boolean().default(true),
  imageFileIds: z.array(uuid).max(10).default([]),
});
export const commentSchema = z.object({ content: requiredText(2000, 'Nội dung bình luận') });

// ---------- Mẫu báo giá ----------
export const templateCreateSchema = z.object({
  name: requiredText(150, 'Tên mẫu'),
  kind: z.enum(TEMPLATE_KINDS),
  description: optionalText(1000),
});

// ---------- Cài đặt ----------
export const companySettingsSchema = z.object({
  name: requiredText(250, 'Tên doanh nghiệp'),
  shortName: optionalText(100),
  address: optionalText(300),
  phone: optionalText(50),
  hotline: optionalText(50),
  email: optionalText(150),
  website: optionalText(200),
  taxCode: optionalText(30),
  bankAccount: optionalText(300),
  representative: optionalText(150),
  representativeTitle: optionalText(100),
  logoFileId: uuid.nullable().optional(),
});

export const quoteSectionSchema = z.object({
  kind: z.enum(TEMPLATE_KINDS),
  enabled: z.boolean(),
  showHeaderFooter: z.boolean(),
});

export const quotePolicySchema = z.object({
  codePrefix: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9-]{1,10}$/),
  validityDays: intRange(1, 365),
  maxDiscountPercent: percent,
  allowAmountDiscount: z.boolean(),
  vatEnabled: z.boolean(),
  vatPercent: percent,
  dcAcRatioMin: optionalDecimalStr(2, 3),
  dcAcRatioMax: optionalDecimalStr(2, 3),
  /**
   * Giá mặc định mỗi tấm pin khi báo giá TĂNG / GIẢM số tấm so với cấu hình chuẩn — dùng cho gói chọn "Đơn giá chênh
   * lệch riêng" mà để trống giá của gói (gói nhập giá riêng ⇒ ghi đè).
   */
  panelIncreaseUnitPrice: optionalMoney,
  panelDecreaseUnitPrice: optionalMoney,
  pricesIncludeVatNote: optionalText(300),
  sections: z.array(quoteSectionSchema).min(1),
  paper: z.object({
    format: z.enum(['A4', 'A5', 'Letter']).default('A4'),
    landscape: z.boolean().default(false),
    marginTopMm: intRange(0, 50),
    marginBottomMm: intRange(0, 50),
    marginLeftMm: intRange(0, 50),
    marginRightMm: intRange(0, 50),
  }),
});
export type CompanySettings = z.infer<typeof companySettingsSchema>;
export type QuotePolicy = z.infer<typeof quotePolicySchema>;
